import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigation } from '@react-navigation/native';

import { useAppDispatch } from '@store/hooks/useAppDispatch';
import {
  selectDashboardDateRangeSelection,
  selectDashboardScope,
  selectDashboardResultType,
  setDashboardScope,
  setDashboardResultType,
  setDashboardListOrderBy,
} from '@reducers/v2/dashboard/dashboard.slice';
import { DashboardScope, DashboardResultType, DashboardResultOrder } from '@api/domain/types';
import type { DashboardResult } from '@api/domain/types';
import { useGetDashboardOverall, useGetDashboardPerScopeValue } from '@api/modules/dashboard/hooks';
import { DASHBOARD_OVERVIEW_BOTTOM_BLOCK_PAGE_SIZE } from '@v2/pages/dashboard/common/constants/rows';
import { DashboardShowAllScreenName } from '@v2/pages/dashboard/navigation/config';
import { getDashboardEntityScreenTarget } from '@v2/pages/dashboard/common/helpers';

export const useDashboardOverviewViewModel = () => {
  const dispatch = useAppDispatch();
  const { from, to } = useSelector(selectDashboardDateRangeSelection);
  const scope = useSelector(selectDashboardScope);
  const resultType = useSelector(selectDashboardResultType);

  const { data: overall, loading: overallLoading, error: overallError, refetch: refetchOverall } = useGetDashboardOverall({
    scope,
    resultType,
    from,
    to,
  });

  const {
    data: worst,
    loading: worstLoading,
    error: worstError,
  } = useGetDashboardPerScopeValue({
    scope,
    resultType,
    from,
    to,
    orderBy: DashboardResultOrder.WorstToBest,
    pagination: { pageNumber: 1, pageSize: DASHBOARD_OVERVIEW_BOTTOM_BLOCK_PAGE_SIZE, includeCount: false },
    enabled: resultType !== DashboardResultType.Actual,
  });

  const {
    data: best,
    loading: bestLoading,
    error: bestError,
  } = useGetDashboardPerScopeValue({
    scope,
    resultType,
    from,
    to,
    orderBy: DashboardResultOrder.BestToWorst,
    pagination: { pageNumber: 1, pageSize: DASHBOARD_OVERVIEW_BOTTOM_BLOCK_PAGE_SIZE, includeCount: false },
  });

  const onSelectScope = useCallback((next: DashboardScope) => dispatch(setDashboardScope(next)), [dispatch]);
  const onSelectResultType = useCallback((next: DashboardResultType) => dispatch(setDashboardResultType(next)), [dispatch]);

  const navigation = useNavigation();

  const navigateToShowAll = useCallback(
    (orderBy: DashboardResultOrder) => {
      dispatch(setDashboardListOrderBy(orderBy));
      navigation.navigate(DashboardShowAllScreenName as never);
    },
    [dispatch, navigation]
  );

  const onShowAllWorst = useCallback(() => navigateToShowAll(DashboardResultOrder.WorstToBest), [navigateToShowAll]);
  const onShowAllBest = useCallback(() => navigateToShowAll(DashboardResultOrder.BestToWorst), [navigateToShowAll]);
  const onShowAllActual = useCallback(() => navigateToShowAll(DashboardResultOrder.BestToWorst), [navigateToShowAll]);

  const onRowPress = useCallback(
    (item: DashboardResult) => {
      const target = getDashboardEntityScreenTarget(scope, item);
      if (!target) return;
      navigation.navigate(target.screenName as never, target.params as never);
    },
    [navigation, scope]
  );

  return {
    scope,
    resultType,
    overall,
    loading: overallLoading,
    error: overallError,
    refetch: refetchOverall,
    worst,
    worstLoading,
    worstError,
    best,
    bestLoading,
    bestError,
    onSelectScope,
    onSelectResultType,
    onShowAllWorst,
    onShowAllBest,
    onShowAllActual,
    onRowPress,
  };
};
